/**
 * The day-streak chip, its celebrations and the week card (specs/day-streak.md §5).
 *
 * The symbol is the ABC Star mascot from the title logo. Asleep (eyes shut, a little dim, a "z"
 * drifting up) means "not played today"; awake means today counted; the longer the streak, the more
 * it sparkles, then a halo, then a crown. A missed day leaves it asleep — never dead, never "lost".
 *
 * Nothing here needs a new asset: the star is the `abc_star` texture preloaded for the whole game,
 * and the sleepy face is drawn over it with a few shapes whose positions were measured off
 * `public/images/abc-star.png` (256×245): eyes at x 105.5 / 149.5, y 88; mouth centre (127.5, 119);
 * face skin ≈ #fac93c. A child who cannot read still gets it: "wake the star up".
 */
import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import {
  addDays, starTier, localYmd, type StreakEvent, type StreakView,
} from '@/game/progression/streak';
import { currentStreakView } from '@/game/progression/streakSession';
import { showPromoCard } from '@/game/ui/promoCard';
import { CHIP_RIGHT, CHIP_TOP, CHIP_H, CHIP_MAX_W } from '@/game/ui/menuSlots';
import {
  playRecordSound, playJourneyFanfare, playStarSound, speakExplanation, cancelRulePrompt,
} from '@/game/audio';

export { CHIP_RIGHT, CHIP_TOP, CHIP_H, CHIP_MAX_W };

/** The texture's native size and the measured face landmarks (see the header). */
const NATIVE_W = 256;
const NATIVE_H = 245;
const EYES = [{ x: 105.5, y: 88 }, { x: 149.5, y: 88 }];
const MOUTH = { x: 127.5, y: 119 };
const SKIN = { r: 250, g: 201, b: 60 };
const NAVY = 0x1c2d5e;
const GOLD = 0xffd166;

export function reducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** A four-point sparkle centred on (cx, cy). */
function sparklePoints(cx: number, cy: number, r: number): { x: number; y: number }[] {
  const k = r * 0.28;
  return [
    { x: cx, y: cy - r }, { x: cx + k, y: cy - k }, { x: cx + r, y: cy }, { x: cx + k, y: cy + k },
    { x: cx, y: cy + r }, { x: cx - k, y: cy + k }, { x: cx - r, y: cy }, { x: cx - k, y: cy - k },
  ];
}

/** What the star looks like: asleep or awake, and how washed-out when the streak is cold. */
export interface StarLook {
  asleep: boolean;
  /** Multiplied into the image (0xffffff = untouched). Used for the cold, greyed-out star. */
  tint: number;
  alpha: number;
}

export function lookFor(view: StreakView, forceAsleep = false): StarLook {
  // Dim by TINT, never by alpha: the sleepy face is a patch drawn over the image, and a container's
  // alpha is applied per child, so the open eyes would show through the patch. The patch is tinted
  // to match (skinColor), which keeps both layers opaque.
  if (!view.started) return { asleep: true, tint: 0xaab4cc, alpha: 1 };
  switch (view.state) {
    case 'lit': return { asleep: forceAsleep, tint: 0xffffff, alpha: 1 };
    case 'pending': return { asleep: true, tint: 0xe6e9f4, alpha: 1 };
    case 'rescuable': return { asleep: true, tint: 0xe6e9f4, alpha: 1 };
    default: return { asleep: true, tint: 0x9aa7bf, alpha: 1 };
  }
}

/** The patch colour that matches the face after the image has been tinted. */
function skinColor(tint: number): number {
  const r = Math.round(SKIN.r * ((tint >> 16) & 0xff) / 255);
  const g = Math.round(SKIN.g * ((tint >> 8) & 0xff) / 255);
  const b = Math.round(SKIN.b * (tint & 0xff) / 255);
  return (r << 16) | (g << 8) | b;
}

/**
 * The star, `height` px tall, centred on the container's origin. Asleep draws the shut face over
 * the open one. Returns the container so callers can tween it as a whole.
 */
export function makeStar(
  scene: Phaser.Scene, height: number, look: StarLook,
): Phaser.GameObjects.Container {
  const img = scene.add.image(0, 0, 'abc_star').setOrigin(0.5).setTint(look.tint);
  const parts: Phaser.GameObjects.GameObject[] = [img];

  if (look.asleep) {
    const g = scene.add.graphics();
    const ox = -NATIVE_W / 2;
    const oy = -NATIVE_H / 2;
    // Cover the open eyes, brows and mouth with the face's own colour...
    g.fillStyle(skinColor(look.tint), 1);
    for (const e of EYES) g.fillRoundedRect(ox + e.x - 15, oy + e.y - 27, 30, 46, 12);
    g.fillRoundedRect(ox + MOUTH.x - 24, oy + MOUTH.y - 17, 48, 34, 14);
    // ...then draw the shut ones: two downward arcs and a small round "o" of a sleeper's mouth.
    g.lineStyle(9, NAVY, 1);
    for (const e of EYES) {
      g.beginPath();
      g.arc(ox + e.x, oy + e.y - 4, 11, Phaser.Math.DegToRad(20), Phaser.Math.DegToRad(160), false);
      g.strokePath();
    }
    g.fillStyle(NAVY, 1);
    g.fillEllipse(ox + MOUTH.x, oy + MOUTH.y, 14, 10);
    parts.push(g);
  }

  const c = scene.add.container(0, 0, parts);
  c.setScale(height / NATIVE_H);
  c.setAlpha(look.alpha);
  return c;
}

/**
 * The streak's rewards, drawn around a star of `height` px centred on (0, 0): sparkles from day 3,
 * a halo from day 7, more sparkles from 14, a crown at 30 (specs/day-streak.md §5.2).
 */
export function drawDecor(
  g: Phaser.GameObjects.Graphics, tier: 1 | 2 | 3 | 4 | 5, height: number, halo = true,
): void {
  g.clear();
  const h = height;
  if (tier >= 3 && halo) {
    g.lineStyle(Math.max(2, h * 0.04), GOLD, 0.65);
    g.strokeCircle(0, h * 0.02, h * 0.6);
  }
  g.fillStyle(0xfff4c2, 1);
  if (tier >= 2) {
    g.fillPoints(sparklePoints(-h * 0.62, -h * 0.34, h * 0.13), true);
    g.fillPoints(sparklePoints(h * 0.64, -h * 0.1, h * 0.1), true);
  }
  if (tier >= 4) {
    g.fillPoints(sparklePoints(h * 0.5, -h * 0.55, h * 0.11), true);
    g.fillPoints(sparklePoints(-h * 0.55, h * 0.4, h * 0.09), true);
  }
  if (tier === 5) {
    // A small crown on the top point.
    const cy = -h * 0.55;
    const w = h * 0.34;
    const ch = h * 0.2;
    const crown = [
      { x: -w / 2, y: cy + ch }, { x: -w / 2, y: cy }, { x: -w / 4, y: cy + ch * 0.45 },
      { x: 0, y: cy - ch * 0.15 }, { x: w / 4, y: cy + ch * 0.45 }, { x: w / 2, y: cy },
      { x: w / 2, y: cy + ch },
    ];
    g.fillStyle(0xffb703, 1);
    g.fillPoints(crown, true);
    g.lineStyle(Math.max(1, h * 0.02), 0x8a5a00, 1);
    g.strokePoints(crown, true, true);
  }
}

export class StreakChip {
  private readonly scene: Phaser.Scene;
  private readonly container: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly decor: Phaser.GameObjects.Graphics;
  private readonly num: Phaser.GameObjects.Text;
  private readonly badge: Phaser.GameObjects.Text;
  private readonly zs: Phaser.GameObjects.Text[];
  private readonly hit: Phaser.GameObjects.Zone;
  private star: Phaser.GameObjects.Container | null = null;
  private tweens: Phaser.Tweens.Tween[] = [];
  private width = 120;

  constructor(scene: Phaser.Scene, view: StreakView, opts: { interactive?: boolean } = {}) {
    this.scene = scene;
    this.bg = scene.add.graphics();
    this.decor = scene.add.graphics();
    this.num = scene.add.text(0, 0, '', {
      fontFamily: FONT_STACK, fontSize: '32px', fontStyle: 'bold', color: '#ffffff',
      stroke: '#0b1e3a', strokeThickness: 5,
    }).setOrigin(0, 0.5);
    this.badge = scene.add.text(0, 0, '🌙', { fontFamily: FONT_STACK, fontSize: '18px' }).setOrigin(0.5);
    this.zs = [0, 1].map((i) => scene.add.text(0, 0, 'z', {
      fontFamily: FONT_STACK, fontSize: `${i === 0 ? 16 : 12}px`, fontStyle: 'bold', color: '#cfe0ff',
    }).setOrigin(0.5).setAlpha(0));
    this.container = scene.add.container(0, 0, [this.bg, this.decor, this.num, this.badge, ...this.zs]);
    this.container.setDepth(20);
    this.hit = scene.add.zone(0, 0, 10, 10);
    if (opts.interactive !== false) {
      this.hit.setInteractive({ useHandCursor: true });
      this.hit.on('pointerdown', () => openStreakCard(scene));
    }
    this.render(view);
  }

  /** (Re)draw for `view`. Safe to call repeatedly. */
  render(view: StreakView, o: { shownCurrent?: number; forceAsleep?: boolean } = {}): void {
    const shown = o.shownCurrent ?? view.current;
    const look = lookFor(view, o.forceAsleep);
    const tier = starTier(Math.max(1, shown));
    const STAR_H = 46;

    this.num.setText(view.started ? String(shown) : '');
    this.num.setColor(view.state === 'cold' ? '#8fa3b8' : '#ffffff');
    const numW = view.started ? this.num.width : 0;
    this.width = Math.min(CHIP_MAX_W, Math.max(64, 66 + numW + (view.started ? 14 : 0)));

    const left = CHIP_RIGHT - this.width;
    this.container.setPosition(left, CHIP_TOP);
    this.hit.setPosition(left + this.width / 2, CHIP_TOP + CHIP_H / 2).setSize(this.width + 20, CHIP_H + 16);

    this.bg.clear();
    this.bg.fillStyle(0x0b1e3a, view.started ? 0.72 : 0.4);
    this.bg.fillRoundedRect(0, 0, this.width, CHIP_H, 28);
    // From day 7 the chip's own border thickens, standing in for the halo the big star gets.
    this.bg.lineStyle(look.asleep ? 2 : tier >= 5 ? 4 : tier >= 3 ? 3 : 2, look.asleep ? 0x7a8da3 : GOLD, view.started ? 0.9 : 0.45);
    this.bg.strokeRoundedRect(0, 0, this.width, CHIP_H, 28);

    const sx = 32;
    const sy = CHIP_H / 2;
    this.tweens.forEach((t) => t.stop());
    this.tweens = [];
    this.star?.destroy();
    this.star = makeStar(this.scene, STAR_H, look);
    this.star.setPosition(sx, sy);
    this.container.add(this.star);
    this.container.bringToTop(this.num);

    this.decor.setPosition(sx, sy);
    if (look.asleep || !view.started) this.decor.clear(); else drawDecor(this.decor, tier, STAR_H, false);   // no halo at chip size: it would fight the border

    this.num.setPosition(60, CHIP_H / 2 + 1);
    this.badge.setPosition(sx + 17, CHIP_H - 9).setVisible(view.state === 'rescuable');

    this.zs.forEach((z) => z.setAlpha(0));
    if (look.asleep && !reducedMotion()) {
      // The sleeper breathes, and a "z" drifts up from it.
      this.tweens.push(this.scene.tweens.add({
        targets: this.star, scale: { from: this.star.scale, to: this.star.scale * 1.05 }, duration: 1500,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      }));
      this.zs.forEach((z, i) => {
        z.setPosition(sx + 16 + i * 6, sy - 14);
        this.tweens.push(this.scene.tweens.add({
          targets: z, y: sy - 30 - i * 3, alpha: { from: 0.9, to: 0 }, duration: 1800, repeat: -1,
          delay: i * 900, ease: 'Sine.easeOut',
        }));
      });
    } else if (look.asleep) {
      this.zs[0].setPosition(sx + 16, sy - 20).setAlpha(0.8);
    }
  }

  /** Celebrate what just happened (spec §5.3). The chip's data already shows today's state. */
  playEvent(ev: StreakEvent): void {
    const view = currentStreakView();
    const fast = reducedMotion();
    const before = ev.kind === 'extend' || ev.kind === 'saved' ? Math.max(0, ev.current - 1) : ev.current;
    playRecordSound();

    if (fast) {
      this.render(view);
    } else {
      // Asleep first, then it wakes: eyes open, a bounce, a puff of sparkles, the number ticks up.
      this.render(view, { forceAsleep: true, shownCurrent: before });
      this.scene.time.delayedCall(450, () => {
        this.render(view);
        if (this.star) {
          const full = this.star.scale;
          this.star.setScale(full * 0.8);
          this.scene.tweens.add({ targets: this.star, scale: full, duration: 420, ease: 'Back.easeOut' });
        }
        this.sparkleBurst();
      });
    }

    if (ev.kind === 'saved') {
      playStarSound();
      this.moonDrift();
    }
    if (ev.milestone !== undefined) {
      this.scene.time.delayedCall(fast ? 0 : 1300, () => playMilestoneBurst(this.scene, ev.milestone!));
    }
  }

  private sparkleBurst(): void {
    const cx = this.container.x + 32;
    const cy = this.container.y + CHIP_H / 2;
    for (let i = 0; i < 7; i++) {
      const s = this.scene.add.text(cx, cy, '✦', {
        fontFamily: FONT_STACK, fontSize: `${12 + (i % 3) * 4}px`, color: '#fff4c2',
      }).setOrigin(0.5).setDepth(30);
      const a = (i / 7) * Math.PI * 2;
      this.scene.tweens.add({
        targets: s, x: cx + Math.cos(a) * 44, y: cy + Math.sin(a) * 36, alpha: { from: 1, to: 0 },
        duration: 700, ease: 'Cubic.easeOut', onComplete: () => s.destroy(),
      });
    }
  }

  private moonDrift(): void {
    const moon = this.scene.add.text(this.container.x + 46, this.container.y - 26, '🌙', {
      fontFamily: FONT_STACK, fontSize: '26px',
    }).setOrigin(0.5).setDepth(30);
    if (reducedMotion()) { this.scene.time.delayedCall(900, () => moon.destroy()); return; }
    this.scene.tweens.add({
      targets: moon, y: this.container.y + CHIP_H - 14, alpha: { from: 1, to: 0 }, duration: 1300,
      ease: 'Sine.easeIn', onComplete: () => moon.destroy(),
    });
  }

  destroy(): void {
    this.tweens.forEach((t) => t.stop());
    this.container.destroy();
    this.hit.destroy();
  }
}

/** Full-screen moment for a milestone day: the big awake star, the number, a ring of sparkles. Tap to skip. */
export function playMilestoneBurst(scene: Phaser.Scene, days: number): void {
  const fast = reducedMotion();
  const cx = GAME_WIDTH / 2;
  const cy = GAME_HEIGHT / 2 - 20;
  const layer: Phaser.GameObjects.GameObject[] = [];
  const dim = scene.add.rectangle(cx, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x050a18, 0.62)
    .setDepth(900).setInteractive();
  layer.push(dim);

  const H = 190;
  const g = scene.add.graphics();
  drawDecor(g, starTier(days), H);
  const star = makeStar(scene, H, { asleep: false, tint: 0xffffff, alpha: 1 });
  const hero = scene.add.container(cx, cy + 40, [g, star]).setDepth(901);
  layer.push(hero);

  const label = i18next.t('streak.milestone', { count: days });
  const text = scene.add.text(cx, cy + 190, label, {
    fontFamily: FONT_STACK, fontSize: '56px', fontStyle: 'bold', color: '#ffd166',
    stroke: '#0b1e3a', strokeThickness: 8,
  }).setOrigin(0.5).setDepth(901);
  layer.push(text);

  if (!fast) {
    hero.setScale(0.2);
    scene.tweens.add({ targets: hero, scale: 1, duration: 700, ease: 'Back.easeOut' });
    scene.tweens.add({
      targets: hero, angle: { from: -4, to: 4 }, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    for (let i = 0; i < 14; i++) {
      const s = scene.add.text(cx, cy + 40, '✦', {
        fontFamily: FONT_STACK, fontSize: `${22 + (i % 3) * 8}px`, color: '#ffe08a',
      }).setOrigin(0.5).setDepth(901);
      layer.push(s);
      const ang = (i / 14) * Math.PI * 2;
      scene.tweens.add({
        targets: s, x: cx + Math.cos(ang) * (250 + (i % 4) * 40), y: cy + 40 + Math.sin(ang) * (170 + (i % 3) * 30),
        alpha: { from: 1, to: 0 }, duration: 1600, delay: 150 + i * 30, ease: 'Cubic.easeOut',
      });
    }
  }

  playJourneyFanfare();
  speakExplanation(label, i18next.language);

  // The Menu's controls are HTML in Phaser's DOM layer, which always draws above the canvas — they
  // would sit on top of the burst. Hide the layer for its few seconds and give it back, whichever
  // way it ends (tap, timer, or the scene going away under it).
  const domLayer = scene.game.domContainer;
  const prevVisibility = domLayer?.style.visibility ?? '';
  if (domLayer) domLayer.style.visibility = 'hidden';

  let done = false;
  const close = (): void => {
    if (done) return;
    done = true;
    cancelRulePrompt();
    if (domLayer) domLayer.style.visibility = prevVisibility;
    layer.forEach((o) => { if (o.scene) o.destroy(); });
  };
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, close);
  dim.on('pointerdown', close);
  scene.time.delayedCall(3200, close);
}

// ── week card ────────────────────────────────────────────────────────────────

let cardOpen = false;

function weekdayInitial(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  try {
    return new Intl.DateTimeFormat(i18next.language, { weekday: 'narrow' }).format(new Date(y, m - 1, d));
  } catch {
    return '';
  }
}

function weekStrip(view: StreakView, today: string): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;justify-content:center;gap:10px;margin:10px 0 6px';
  view.week.forEach((played, i) => {
    const cell = document.createElement('div');
    cell.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:4px;font-size:13px;color:#aabbcc';
    const dot = document.createElement('div');
    const isToday = i === 6;
    // A played day is a small awake star, a missed one an empty ring: never colour alone.
    dot.style.cssText = 'width:30px;height:30px;display:flex;align-items:center;justify-content:center;'
      + 'box-sizing:border-box;border-radius:50%;font-size:20px;line-height:1;'
      + `${played ? '' : 'border:3px solid #5a6a7c;'}`
      + `${isToday ? 'box-shadow:0 0 0 3px rgba(255,255,255,.55);' : ''}`;
    if (played) dot.textContent = '⭐';
    const label = document.createElement('span');
    label.textContent = weekdayInitial(addDays(today, i - 6));
    cell.append(dot, label);
    row.append(cell);
  });
  return row;
}

/** Tap the chip: the week strip, best, rest days and the next milestone. Read aloud on open. */
export function openStreakCard(scene: Phaser.Scene): void {
  if (cardOpen) return;
  cardOpen = true;
  const view = currentStreakView();
  const today = localYmd();

  const title = view.started
    ? i18next.t('streak.days', { count: view.current })
    : i18next.t('streak.title');
  const lines: string[] = [];
  if (view.best > 0) lines.push(i18next.t('streak.best', { count: view.best }));
  if (view.state === 'rescuable') lines.push(`🌙 ${i18next.t('streak.restDay')}`);
  else if (view.freezes > 0) lines.push(`${'🌙'.repeat(view.freezes)} ${i18next.t('streak.restDays', { count: view.freezes })}`);
  if (view.nextMilestone !== null && view.started) {
    lines.push(i18next.t('streak.next', { count: Math.max(1, view.nextMilestone - view.current) }));
  }
  if (lines.length === 0) lines.push(i18next.t('streak.intro'));

  showPromoCard(scene, {
    title: `⭐ ${title}`,
    lines,
    extra: weekStrip(view, today),
    actions: [{ label: i18next.t('nav.ok'), kind: 'primary' }],
  }, {
    closeOnOutsideTap: true,
    onDismiss: () => { cancelRulePrompt(); cardOpen = false; },
  });
  speakExplanation(title, i18next.language);
}
